import { getCountrySlugCandidates } from '~/utils/countrySlug'
import {
  NOTIFICATION_DISPLAY_TYPES,
  NOTIFICATION_ADDITIONAL_CONDITION_TYPES,
  NOTIFICATION_LOGIN_STATES,
  NOTIFICATION_ROLE_NAMES,
  NOTIFICATION_SELECTOR_MODES,
  type NotificationAudienceContext,
  type NotificationAdditionalCondition,
  type NotificationAdditionalConditionType,
  type NotificationDiagnostic,
  type NotificationEligibilityResult,
  type NotificationEvaluationOptions,
  type NotificationEvaluationResult,
  type NotificationLogger,
  type NotificationLoginState,
  type NotificationRoleName,
  type NotificationSelectorMode,
  type NotificationSource,
  type SiteNotification,
  type SiteNotificationAudience,
  type SiteNotificationCta,
  type SiteNotificationRegistryReference,
} from '~/types/siteNotification'

type UnknownRecord = Record<string, unknown>

interface NotificationCandidateMetadata {
  source: NotificationSource
  sourceOrder: number
}

interface NormalizationOptions {
  source?: NotificationSource
  sourceOrder?: number
  routeSlug?: string | string[] | null
  logger?: NotificationLogger
  warnings?: NotificationDiagnostic[]
  errors?: NotificationDiagnostic[]
}

interface NormalizedStore {
  id: string
  country: string
  roles: NotificationRoleName[]
  acceptedTermsAndConditionsAt?: string | null
}

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const asTrimmedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

const getEntryFields = (entry: UnknownRecord): UnknownRecord | undefined => {
  if (isRecord(entry.fields)) return entry.fields
  return entry
}

/**
 * Read an optional registry field across Contentful response modifiers.
 * Unresolved, absent, or malformed fields are intentionally empty.
 */
export const getSiteNotificationReferences = (
  entry: unknown,
): readonly SiteNotificationRegistryReference[] => {
  if (!isRecord(entry)) return []
  const fields = isRecord(entry.fields) ? entry.fields : undefined
  return Array.isArray(fields?.notifications)
    ? fields.notifications.filter(
        (reference): reference is SiteNotificationRegistryReference =>
          isRecord(reference) && isRecord(reference.fields),
      )
    : []
}

const getEntryId = (entry: unknown): string => {
  if (!isRecord(entry)) return ''
  const sys = isRecord(entry.sys) ? entry.sys : undefined
  return asTrimmedString(sys?.id) || asTrimmedString(entry.id)
}

const getTagValue = (value: unknown): string => {
  if (typeof value === 'string') return value.trim()
  if (!isRecord(value)) return ''

  const fields = isRecord(value.fields) ? value.fields : undefined
  return asTrimmedString(fields?.value) || asTrimmedString(value.value)
}

const getArrayField = (
  fields: UnknownRecord,
  key: string,
): unknown[] | undefined => {
  const value = fields[key]
  if (value === undefined || value === null) return undefined
  return Array.isArray(value) ? value : undefined
}

const routeKey = (routeSlug: string | string[] | null | undefined): string =>
  Array.isArray(routeSlug) ? routeSlug.join('/') : routeSlug || ''

const emitDiagnostic = (
  diagnostic: NotificationDiagnostic,
  logger: NotificationLogger | undefined,
  collection: NotificationDiagnostic[],
) => {
  collection.push(diagnostic)
  const message = diagnostic.message

  if (diagnostic.level === 'error') {
    if (logger?.error) {
      logger.error(message, diagnostic)
    } else {
      console.error(message, diagnostic)
    }
  } else {
    if (logger?.warn) {
      logger.warn(message, diagnostic)
    } else {
      console.warn(message, diagnostic)
    }
  }
}

const warning = (
  entryId: string,
  code: string,
  message: string,
  options: NormalizationOptions,
  details: Pick<NotificationDiagnostic, 'dimension' | 'rawValue'> = {},
) => {
  emitDiagnostic(
    {
      level: 'warning',
      code,
      entryId,
      message,
      ...details,
    },
    options.logger,
    options.warnings || [],
  )
}

const error = (
  entryId: string,
  code: string,
  message: string,
  options: NormalizationOptions,
  details: Pick<NotificationDiagnostic, 'dimension' | 'rawValue'> = {},
) => {
  emitDiagnostic(
    {
      level: 'error',
      code,
      entryId,
      message,
      ...details,
    },
    options.logger,
    options.errors || [],
  )
}

const normalizeSelector = (
  value: unknown,
): NotificationSelectorMode | undefined =>
  typeof value === 'string' &&
  (NOTIFICATION_SELECTOR_MODES as readonly string[]).includes(value.trim())
    ? (value.trim() as NotificationSelectorMode)
    : undefined

const normalizeRule = (
  fields: UnknownRecord,
  entryId: string,
  dimension: string,
  selectorKey: string,
  valuesKey: string,
  options: NormalizationOptions,
  normalizeValue: (value: unknown) => string[],
) => {
  const rawSelector = fields[selectorKey]
  const rawValues = fields[valuesKey]
  const hasSelector = rawSelector !== undefined && rawSelector !== null
  const hasValues = rawValues !== undefined && rawValues !== null

  if (!hasSelector && !hasValues) return undefined

  const selector = normalizeSelector(rawSelector)
  if (!selector) {
    warning(
      entryId,
      'invalid-selector',
      `Ignoring malformed ${dimension} selector for Notification ${entryId}.`,
      options,
      { dimension, rawValue: rawSelector },
    )
    return undefined
  }

  const rawValueList = getArrayField(fields, valuesKey)
  if (!rawValueList) {
    warning(
      entryId,
      'invalid-values',
      `Ignoring malformed ${dimension} values for Notification ${entryId}.`,
      options,
      { dimension, rawValue: rawValues },
    )
    return undefined
  }

  const values: string[] = []
  for (const rawValue of rawValueList) {
    const normalizedValues = normalizeValue(rawValue)
    if (!normalizedValues.length) {
      warning(
        entryId,
        'invalid-value',
        `Ignoring an unusable ${dimension} value for Notification ${entryId}.`,
        options,
        { dimension, rawValue },
      )
      continue
    }
    values.push(...normalizedValues)
  }

  const uniqueValues = Array.from(new Set(values))
  if (!uniqueValues.length) {
    warning(
      entryId,
      'empty-values',
      `Ignoring ${dimension} rules without usable values for Notification ${entryId}.`,
      options,
      { dimension },
    )
    return undefined
  }

  return { selector, values: uniqueValues }
}

const normalizeLanguageValue = (value: unknown): string[] => {
  const normalized = getTagValue(value)
  return normalized ? [normalized] : []
}

const normalizeCountryValue = (value: unknown): string[] => {
  const normalized = getTagValue(value)
  if (!normalized) return []

  // Location tags normally already contain `country-`; accepting ISO codes
  // and names here keeps fixtures and adapters aligned with the user-country
  // normalization contract as well.
  return getCountrySlugCandidates(normalized)
}

const normalizeStoreIdValue = (value: unknown): string[] => {
  const normalized = asTrimmedString(value)
  return normalized ? [normalized] : []
}

const normalizeRoleValue = (value: unknown): string[] => {
  const normalized = asTrimmedString(value)
  return (NOTIFICATION_ROLE_NAMES as readonly string[]).includes(normalized)
    ? [normalized]
    : []
}

const normalizeAdditionalConditionType = (
  value: unknown,
): NotificationAdditionalConditionType | undefined => {
  const normalized = asTrimmedString(value)
  return (
    NOTIFICATION_ADDITIONAL_CONDITION_TYPES as readonly string[]
  ).includes(normalized)
    ? (normalized as NotificationAdditionalConditionType)
    : undefined
}

/**
 * Preserve the authored ISO-8601 offset. Eligibility compares parsed instants,
 * so the custom app's fixed -08:00 convention and EventLink's timestamps can
 * safely use different offsets.
 */
const normalizeEffectiveTimestamp = (value: unknown): string | undefined => {
  const normalized = asTrimmedString(value)
  if (!normalized || Number.isNaN(Date.parse(normalized))) return undefined
  return normalized
}

const getCtaFields = (cta: SiteNotificationCta): UnknownRecord =>
  isRecord(cta.fields) ? cta.fields : {}

const asCta = (value: unknown): SiteNotificationCta | undefined => {
  if (!isRecord(value)) return undefined

  const fields = isRecord(value.fields) ? value.fields : value
  const id = getEntryId(value)
  return {
    ...value,
    ...(id ? { id } : {}),
    fields,
  }
}

/** Apply the same route-specific CTA override used by the shared CTA. */
export const resolveNotificationCta = (
  cta: SiteNotificationCta | undefined,
  routeSlug?: string | string[] | null,
): SiteNotificationCta | undefined => {
  if (!cta) return undefined
  const fields = getCtaFields(cta)
  const configOverride = isRecord(fields.configOverride)
    ? fields.configOverride
    : undefined
  const override = configOverride?.[routeKey(routeSlug)]

  if (!isRecord(override)) return cta
  return { ...cta, fields: { ...fields, ...override } }
}

export const isUsableNotificationCta = (
  cta: SiteNotificationCta | undefined,
  routeSlug?: string | string[] | null,
): boolean => {
  const resolvedCta = resolveNotificationCta(cta, routeSlug)
  if (!resolvedCta) return false

  const fields = getCtaFields(resolvedCta)
  const label = asTrimmedString(fields.label)
  return Boolean(label && asTrimmedString(fields.link))
}

/**
 * Convert a resolved Contentful-shaped Notification entry to the small
 * feature-local domain contract. Unresolved links and invalid required fields
 * are omitted before audience evaluation. Contentful marks localized heading
 * and body fields optional, but a renderable Notification still needs both.
 */
export const normalizeNotificationEntry = (
  entry: unknown,
  options: NormalizationOptions = {},
): SiteNotification | undefined => {
  if (!isRecord(entry)) return undefined
  const fields = getEntryFields(entry)
  const entryId = getEntryId(entry)
  if (!fields || !entryId || !isRecord(entry.fields)) return undefined

  const heading = asTrimmedString(fields.heading)
  const body = typeof fields.body === 'string' ? fields.body : ''
  const displayType = asTrimmedString(fields.displayType)
  if (!heading || !body.trim()) {
    error(
      entryId,
      'invalid-content',
      `Suppressing Notification ${entryId} because heading and body are required for rendering.`,
      options,
    )
    return undefined
  }
  if (
    !(NOTIFICATION_DISPLAY_TYPES as readonly string[]).includes(displayType)
  ) {
    error(
      entryId,
      'invalid-display-type',
      `Suppressing Notification ${entryId} because displayType is invalid.`,
      options,
      { rawValue: fields.displayType },
    )
    return undefined
  }

  const rawLoginState = fields.loginState
  if (
    rawLoginState !== undefined &&
    rawLoginState !== null &&
    !(NOTIFICATION_LOGIN_STATES as readonly unknown[]).includes(rawLoginState)
  ) {
    error(
      entryId,
      'invalid-login-state',
      `Suppressing Notification ${entryId} because loginState is invalid.`,
      options,
      { rawValue: rawLoginState },
    )
    return undefined
  }
  const loginState: NotificationLoginState = rawLoginState
    ? (rawLoginState as NotificationLoginState)
    : 'Everyone'

  const rawCtas = getArrayField(fields, 'ctas') || []
  const ctas = rawCtas.map(asCta).filter(Boolean) as SiteNotificationCta[]
  const rawAdditionalCondition = fields.additionalCondition
  const hasAdditionalCondition =
    rawAdditionalCondition !== undefined && rawAdditionalCondition !== null
  let additionalCondition: NotificationAdditionalCondition | undefined

  if (hasAdditionalCondition) {
    if (!isRecord(rawAdditionalCondition)) {
      error(
        entryId,
        'invalid-additional-condition',
        `Suppressing Notification ${entryId} because additionalCondition is not an object.`,
        options,
        { rawValue: rawAdditionalCondition },
      )
      return undefined
    }

    const type = normalizeAdditionalConditionType(rawAdditionalCondition.type)
    if (!type) {
      error(
        entryId,
        'invalid-additional-condition',
        `Suppressing Notification ${entryId} because additionalCondition.type is invalid.`,
        options,
        { rawValue: rawAdditionalCondition.type },
      )
      return undefined
    }

    const params = rawAdditionalCondition.params
    const effectiveAt = isRecord(params)
      ? normalizeEffectiveTimestamp(params.effectiveAt)
      : undefined
    if (!effectiveAt) {
      error(
        entryId,
        'additional-condition-effective-at-required',
        `Suppressing Terms Notification ${entryId} because additionalCondition.params.effectiveAt is missing or invalid.`,
        options,
        { rawValue: isRecord(params) ? params.effectiveAt : params },
      )
      return undefined
    }

    additionalCondition = {
      type,
      params: { effectiveAt },
    }
  }
  const audience: SiteNotificationAudience = {
    language: normalizeRule(
      fields,
      entryId,
      'language',
      'languageInclusionExclusion',
      'languages',
      options,
      normalizeLanguageValue,
    ),
    storeCountry: normalizeRule(
      fields,
      entryId,
      'storeCountry',
      'storeCountryInclusionExclusion',
      'storeCountries',
      options,
      normalizeCountryValue,
    ),
    storeId: normalizeRule(
      fields,
      entryId,
      'storeId',
      'storeIdInclusionExclusion',
      'storeIds',
      options,
      normalizeStoreIdValue,
    ),
    role: normalizeRule(
      fields,
      entryId,
      'role',
      'roleInclusionExclusion',
      'roles',
      options,
      normalizeRoleValue,
    ),
  }

  const normalized: SiteNotification = {
    id: entryId,
    entryTitle: asTrimmedString(fields.entryTitle) || entryId,
    heading,
    body,
    ctas,
    dismissLabel: asTrimmedString(fields.dismissLabel),
    displayType: displayType as SiteNotification['displayType'],
    blocking: fields.blocking === true,
    loginState,
    ...(additionalCondition ? { additionalCondition } : {}),
    audience,
    source: options.source || 'global',
    sourceOrder: options.sourceOrder || 0,
  }

  if (
    normalized.displayType === 'Modal' &&
    normalized.blocking &&
    !normalized.ctas.some((cta) =>
      isUsableNotificationCta(cta, options.routeSlug),
    )
  ) {
    error(
      entryId,
      'blocking-modal-cta-required',
      `Suppressing blocking Modal Notification ${entryId} because it has no usable CTA.`,
      options,
    )
    return undefined
  }

  return normalized
}

const getRoleName = (value: unknown): string => {
  if (typeof value === 'string') return value.trim()
  if (isRecord(value)) return asTrimmedString(value.roleName)
  return ''
}

const getStoreRoles = (store: UnknownRecord): NotificationRoleName[] => {
  const rawRoles = [
    ...(typeof store.roleName === 'string' ? [store.roleName] : []),
    ...(Array.isArray(store.roles) ? store.roles : []),
    ...(Array.isArray(store.roleNames) ? store.roleNames : []),
  ]
  return Array.from(
    new Set(
      rawRoles
        .map(getRoleName)
        .filter((role): role is NotificationRoleName =>
          (NOTIFICATION_ROLE_NAMES as readonly string[]).includes(role),
        ),
    ),
  )
}

const getStoreId = (store: UnknownRecord): string => asTrimmedString(store.id)

const getStoreCountry = (store: UnknownRecord): string =>
  asTrimmedString(store.country) || asTrimmedString(store.storeCountry)

const getAcceptedTermsTimestamp = (
  store: UnknownRecord,
): string | null | undefined => {
  const value = store.acceptedTermsAndConditionsAt
  if (value === null) return null
  return typeof value === 'string' ? value.trim() : undefined
}

const normalizeStores = (
  context: NotificationAudienceContext,
): NormalizedStore[] => {
  const storesById = new Map<string, NormalizedStore>()
  const roleNamesByStoreId = new Map<string, string[]>()

  for (const role of context.roles || []) {
    if (!isRecord(role) || !isRecord(role.organization)) continue
    const id = getStoreId(role.organization)
    const roleName = getRoleName(role)
    if (!id) continue
    const roleNames = roleNamesByStoreId.get(id) || []
    roleNames.push(roleName)
    roleNamesByStoreId.set(id, roleNames)
  }

  const directStores = [
    ...(context.stores || []),
    ...(context.organizations || []),
  ]

  for (const value of directStores) {
    if (!isRecord(value)) continue
    const id = getStoreId(value)
    if (!id) continue
    const roleNamesFromContext = roleNamesByStoreId.get(id)
    if (
      roleNamesFromContext &&
      roleNamesFromContext.length > 0 &&
      !roleNamesFromContext.some((role) =>
        (NOTIFICATION_ROLE_NAMES as readonly string[]).includes(role),
      )
    ) {
      continue
    }
    const roles = getStoreRoles(value)
    // A store represented only by non-participating roles is not eligible for
    // store-scoped audience matching.
    const hasDirectRoleInfo =
      typeof value.roleName === 'string' ||
      (Array.isArray(value.roles) && value.roles.length > 0) ||
      (Array.isArray(value.roleNames) && value.roleNames.length > 0)
    if (hasDirectRoleInfo) {
      const hasKnownRole = roles.length > 0
      if (!hasKnownRole) continue
    }
    const existing = storesById.get(id)
    if (existing) {
      if (!existing.country) existing.country = getStoreCountry(value)
      if (
        existing.acceptedTermsAndConditionsAt === undefined &&
        getAcceptedTermsTimestamp(value) !== undefined
      ) {
        existing.acceptedTermsAndConditionsAt = getAcceptedTermsTimestamp(value)
      }
      for (const role of roles) {
        if (!existing.roles.includes(role)) existing.roles.push(role)
      }
    } else {
      storesById.set(id, {
        id,
        country: getStoreCountry(value),
        roles,
        acceptedTermsAndConditionsAt: getAcceptedTermsTimestamp(value),
      })
    }
  }

  for (const role of context.roles || []) {
    if (!isRecord(role) || !isRecord(role.organization)) continue
    const roleName = getRoleName(role)
    if (!(NOTIFICATION_ROLE_NAMES as readonly string[]).includes(roleName)) {
      continue
    }
    const organization = role.organization
    const id = getStoreId(organization)
    if (!id) continue
    const existing = storesById.get(id)
    if (existing) {
      if (!existing.roles.includes(roleName as NotificationRoleName)) {
        existing.roles.push(roleName as NotificationRoleName)
      }
      if (!existing.country) existing.country = getStoreCountry(organization)
      if (
        existing.acceptedTermsAndConditionsAt === undefined &&
        getAcceptedTermsTimestamp(organization) !== undefined
      ) {
        existing.acceptedTermsAndConditionsAt =
          getAcceptedTermsTimestamp(organization)
      }
    } else {
      storesById.set(id, {
        id,
        country: getStoreCountry(organization),
        roles: [roleName as NotificationRoleName],
        acceptedTermsAndConditionsAt: getAcceptedTermsTimestamp(organization),
      })
    }
  }

  return [...storesById.values()]
}

const includesNormalized = (values: string[], value: string): boolean => {
  const normalized = value.trim().toLowerCase()
  return values.some(
    (candidate) => candidate.trim().toLowerCase() === normalized,
  )
}

const matchesCountryRule = (
  candidates: string[],
  rule: { selector: NotificationSelectorMode; values: string[] },
): boolean => {
  const matches = rule.values.some((value) =>
    candidates.some(
      (candidate) => candidate.toLowerCase() === value.toLowerCase(),
    ),
  )
  return rule.selector === 'inclusion' ? matches : !matches
}

const matchesStoreRule = (
  store: NormalizedStore,
  rule: { selector: NotificationSelectorMode; values: string[] },
  dimension: 'storeId' | 'role',
): boolean => {
  if (dimension === 'storeId') {
    const matches = includesNormalized(rule.values, store.id)
    return rule.selector === 'inclusion' ? matches : !matches
  }

  if (!store.roles.length) return false
  const matches = store.roles.some((role) =>
    includesNormalized(rule.values, role),
  )
  return rule.selector === 'inclusion' ? matches : !matches
}

const matchesTermsCondition = (
  store: NormalizedStore,
  notification: SiteNotification,
  logger: NotificationLogger | undefined,
  warnings: NotificationDiagnostic[] | undefined,
): boolean => {
  if (notification.additionalCondition?.type !== 'termsAcceptanceRequired') {
    return true
  }

  const acceptedAt = store.acceptedTermsAndConditionsAt
  if (acceptedAt === null) return true
  const effectiveAt = notification.additionalCondition.params.effectiveAt
  if (!acceptedAt || !effectiveAt) {
    const diagnostic: NotificationDiagnostic = {
      level: 'warning',
      code: 'missing-terms-acceptance',
      entryId: notification.id,
      dimension: 'additionalCondition',
      rawValue: acceptedAt,
      message: `Ignoring an organization for Terms Notification ${notification.id} because its acceptance timestamp is missing.`,
    }
    warnings?.push(diagnostic)
    logger?.warn?.(diagnostic.message, diagnostic)
    return false
  }

  const acceptedAtMs = Date.parse(acceptedAt)
  const effectiveAtMs = Date.parse(effectiveAt)
  if (Number.isNaN(acceptedAtMs) || Number.isNaN(effectiveAtMs)) {
    const diagnostic: NotificationDiagnostic = {
      level: 'warning',
      code: 'invalid-terms-acceptance',
      entryId: notification.id,
      dimension: 'additionalCondition',
      rawValue: acceptedAt,
      message: `Ignoring an organization for Terms Notification ${notification.id} because its acceptance timestamp is invalid.`,
    }
    warnings?.push(diagnostic)
    logger?.warn?.(diagnostic.message, diagnostic)
    return false
  }

  return acceptedAtMs < effectiveAtMs
}

/** Evaluate one normalized Notification without persistence or authorization. */
export const evaluateNotificationEligibility = (
  notification: SiteNotification,
  context: NotificationAudienceContext,
  logger?: NotificationLogger,
  warnings?: NotificationDiagnostic[],
): NotificationEligibilityResult => {
  if (
    context.ready !== true ||
    (notification.loginState === 'Logged in only' &&
      context.authenticated !== true) ||
    (notification.loginState === 'Logged out only' &&
      context.authenticated !== false)
  ) {
    return { eligible: false, matchedStoreIds: [] }
  }

  const locale = asTrimmedString(context.locale)
  const languageRule = notification.audience.language
  if (languageRule) {
    const matches = includesNormalized(languageRule.values, locale)
    const languagePass =
      languageRule.selector === 'inclusion' ? matches : !matches
    if (!languagePass) return { eligible: false, matchedStoreIds: [] }
  }

  const countryRule = notification.audience.storeCountry
  const requiresOrganization = Boolean(
    notification.audience.storeId || notification.audience.role,
  )
  const hasTermsCondition =
    notification.additionalCondition?.type === 'termsAcceptanceRequired'
  // Logged-out visitors must never inherit organization data from stale context.
  const stores = context.authenticated === true ? normalizeStores(context) : []
  if (!countryRule && !requiresOrganization && !hasTermsCondition) {
    return {
      eligible: true,
      matchedStoreIds: stores.map((store) => store.id),
    }
  }

  if (
    !stores.length &&
    countryRule &&
    !requiresOrganization &&
    !hasTermsCondition
  ) {
    const candidates = getCountrySlugCandidates(
      asTrimmedString(context.visitorCountry),
    )
    return {
      eligible:
        candidates.length > 0 && matchesCountryRule(candidates, countryRule),
      matchedStoreIds: [],
    }
  }
  const matchedStoreIds: string[] = []
  for (const store of stores) {
    let passes = true
    if (notification.audience.storeCountry) {
      // A populated organization country stays authoritative even on a mismatch
      // or an unknown value. Only a missing country falls back to the cookie.
      const candidates = getCountrySlugCandidates(
        store.country || asTrimmedString(context.visitorCountry),
      )
      if (!candidates.length) {
        const diagnostic: NotificationDiagnostic = {
          level: 'warning',
          code: 'unknown-store-country',
          entryId: notification.id,
          dimension: 'storeCountry',
          rawValue: store.country,
          message: `Ignoring a store for Notification ${notification.id} because its country is unknown.`,
        }
        warnings?.push(diagnostic)
        if (logger?.warn) {
          logger.warn(diagnostic.message, diagnostic)
        } else {
          console.warn(diagnostic.message, diagnostic)
        }
        passes = false
      } else {
        passes = matchesCountryRule(
          candidates,
          notification.audience.storeCountry,
        )
      }
    }
    if (passes && notification.audience.storeId) {
      passes = matchesStoreRule(store, notification.audience.storeId, 'storeId')
    }
    if (passes && notification.audience.role) {
      passes = matchesStoreRule(store, notification.audience.role, 'role')
    }
    if (passes) {
      passes = matchesTermsCondition(store, notification, logger, warnings)
    }
    if (passes) matchedStoreIds.push(store.id)
  }

  return {
    eligible: matchedStoreIds.length > 0,
    matchedStoreIds,
  }
}

const sourceEntries = (
  entries: readonly unknown[] | null | undefined,
): unknown[] => (Array.isArray(entries) ? [...entries] : [])

/** Merge, deduplicate, normalize, and evaluate Global/Page entries in priority order. */
export const evaluateNotifications = (
  options: NotificationEvaluationOptions,
): NotificationEvaluationResult => {
  const warnings: NotificationDiagnostic[] = []
  const errors: NotificationDiagnostic[] = []
  if (options.context.ready !== true) {
    return { candidates: [], warnings, errors }
  }

  const seenIds = new Set<string>()
  const rawCandidates: Array<{
    entry: unknown
    metadata: NotificationCandidateMetadata
  }> = []
  const sources: Array<{
    entries: readonly unknown[] | null | undefined
    source: NotificationSource
  }> = [
    { entries: options.globalEntries, source: 'global' },
    { entries: options.pageEntries, source: 'page' },
  ]

  for (const { entries, source } of sources) {
    sourceEntries(entries).forEach((entry, sourceOrder) => {
      // Skip unresolved links before deduplication so they cannot hide a
      // resolved copy of the same entry from the other registry source.
      if (!isRecord(entry) || !isRecord(entry.fields)) return
      const id = getEntryId(entry)
      if (!id || seenIds.has(id)) return
      seenIds.add(id)
      rawCandidates.push({
        entry,
        metadata: { source, sourceOrder },
      })
    })
  }

  const normalized = rawCandidates
    .map(({ entry, metadata }) =>
      normalizeNotificationEntry(entry, {
        ...metadata,
        routeSlug: options.routeSlug,
        logger: options.logger,
        warnings,
        errors,
      }),
    )
    .filter(Boolean) as SiteNotification[]

  normalized.sort((a, b) => {
    if (a.blocking !== b.blocking) return a.blocking ? -1 : 1
    if (a.source !== b.source) return a.source === 'global' ? -1 : 1
    return a.sourceOrder - b.sourceOrder
  })

  const candidates: SiteNotification[] = []
  for (const notification of normalized) {
    const eligibility = evaluateNotificationEligibility(
      notification,
      options.context,
      options.logger,
      warnings,
    )
    if (!eligibility.eligible) continue
    candidates.push({
      ...notification,
      matchedStoreIds: eligibility.matchedStoreIds,
    })
  }

  return { candidates, warnings, errors }
}
